/**
 * CadenceConfigurator
 *
 * Configura cuándo dispara la campaña:
 *   - kind: one_shot (una sola vez) vs recurring (todos los días)
 *   - sendWindow: rango horario permitido
 *   - daysOfWeek: si es recurring, qué días
 *   - scheduledFor: si es one_shot, fecha + hora
 */

import { Calendar, Clock, Repeat, Target } from "lucide-react";
import type { ObjectiveCadence, CadenceKind } from "../types";

interface CadenceConfiguratorProps {
  cadence: ObjectiveCadence;
  onChange: (cadence: ObjectiveCadence) => void;
}

const DAYS = [
  { num: 1, label: "L" },
  { num: 2, label: "M" },
  { num: 3, label: "X" },
  { num: 4, label: "J" },
  { num: 5, label: "V" },
  { num: 6, label: "S" },
  { num: 7, label: "D" },
];

export function CadenceConfigurator({
  cadence,
  onChange,
}: CadenceConfiguratorProps) {
  const setKind = (kind: CadenceKind) =>
    onChange({ ...cadence, kind });

  const setWindow = (field: "start" | "end", value: string) =>
    onChange({
      ...cadence,
      sendWindow: { ...cadence.sendWindow, [field]: value },
    });

  const toggleDay = (day: number) => {
    const current = cadence.daysOfWeek || [];
    const next = current.includes(day)
      ? current.filter((d) => d !== day)
      : [...current, day].sort();
    onChange({ ...cadence, daysOfWeek: next });
  };

  const setScheduledFor = (value: string) =>
    onChange({ ...cadence, scheduledFor: value });

  return (
    <div className="space-y-5">
      {/* Tipo de cadencia */}
      <div>
        <label className="block text-sm font-semibold text-slate-900 mb-2">
          ¿Cuándo se dispara?
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setKind("recurring")}
            className={`p-3 rounded-xl border-2 text-left transition-all ${
              cadence.kind === "recurring"
                ? "border-violet-300 bg-violet-50"
                : "border-slate-100 hover:border-slate-200"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Repeat
                size={14}
                className={
                  cadence.kind === "recurring"
                    ? "text-violet-600"
                    : "text-slate-400"
                }
              />
              <p
                className={`text-sm font-semibold ${
                  cadence.kind === "recurring"
                    ? "text-violet-700"
                    : "text-slate-700"
                }`}
              >
                Recurrente
              </p>
            </div>
            <p className="text-[10px] text-slate-500 leading-tight">
              Se dispara cada vez que un cliente cumple las condiciones.
            </p>
          </button>

          <button
            type="button"
            onClick={() => setKind("one_shot")}
            className={`p-3 rounded-xl border-2 text-left transition-all ${
              cadence.kind === "one_shot"
                ? "border-violet-300 bg-violet-50"
                : "border-slate-100 hover:border-slate-200"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Target
                size={14}
                className={
                  cadence.kind === "one_shot"
                    ? "text-violet-600"
                    : "text-slate-400"
                }
              />
              <p
                className={`text-sm font-semibold ${
                  cadence.kind === "one_shot"
                    ? "text-violet-700"
                    : "text-slate-700"
                }`}
              >
                Una sola vez
              </p>
            </div>
            <p className="text-[10px] text-slate-500 leading-tight">
              Se envía en una fecha y hora específica y se termina.
            </p>
          </button>
        </div>
      </div>

      {/* Ventana horaria */}
      <div>
        <label className="block text-sm font-semibold text-slate-900 mb-2">
          Horario de envío
        </label>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Clock
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="time"
              value={cadence.sendWindow.start}
              onChange={(e) => setWindow("start", e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm
                focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all"
            />
          </div>
          <span className="text-slate-400">a</span>
          <div className="relative flex-1">
            <Clock
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="time"
              value={cadence.sendWindow.end}
              onChange={(e) => setWindow("end", e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm
                focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all"
            />
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
          Los mensajes solo se envían dentro de este horario para no molestar
          fuera del horario de tu negocio.
        </p>
      </div>

      {/* Días (solo recurring) */}
      {cadence.kind === "recurring" && (
        <div className="animate-fade-in">
          <label className="block text-sm font-semibold text-slate-900 mb-2">
            Días de la semana
          </label>
          <div className="flex gap-2">
            {DAYS.map((d) => {
              const isActive = (cadence.daysOfWeek || []).includes(d.num);
              return (
                <button
                  key={d.num}
                  type="button"
                  onClick={() => toggleDay(d.num)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${
                    isActive
                      ? "bg-violet-600 text-white shadow-sm"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
          {(cadence.daysOfWeek || []).length === 0 && (
            <p className="text-[11px] text-red-500 mt-1.5">
              Elegí al menos un día.
            </p>
          )}
        </div>
      )}

      {/* Fecha (solo one_shot) */}
      {cadence.kind === "one_shot" && (
        <div className="animate-fade-in">
          <label className="block text-sm font-semibold text-slate-900 mb-2">
            Fecha y hora exacta
          </label>
          <div className="relative">
            <Calendar
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="datetime-local"
              value={
                cadence.scheduledFor
                  ? cadence.scheduledFor.slice(0, 16)
                  : ""
              }
              onChange={(e) =>
                setScheduledFor(
                  e.target.value
                    ? new Date(e.target.value).toISOString()
                    : "",
                )
              }
              className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm
                focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default CadenceConfigurator;
